/**
 * Minimal, dependency-free feature-flag gate (ALL-5039 / SPEC-HW-8, HW-L9a).
 *
 * sandbox-shell has no feature-flag library today (no LaunchDarkly/Split/etc.
 * dependency in apps/sandbox-shell/package.json), so this introduces one
 * reusable primitive rather than a one-off gate for hot wallets alone.
 *
 * A flag is BUILD-TIME ONLY: it is on when the Vite env var
 * `VITE_FEATURE_<FLAG>` is a truthy string ("1", "true", "on"), read via
 * `import.meta.env` and fixed per environment at build time. There is no
 * runtime override (no localStorage, query string or cookie switch), so no
 * browser can turn on a gate that its build shipped off.
 *
 * `hot_wallets` is OFF by default: HW-L9a ships the section dark, per
 * LANES.md HW-L9a ("fixture-free page shells behind a feature flag").
 */

export type FeatureFlag = "hot_wallets" | "deferred_pages" | "invoicing";

const DEFAULTS: Record<FeatureFlag, boolean> = {
  hot_wallets: false,
  // Treasury, Governance, Monitoring, Reconciliation, Evidence and Recovery have no
  // backend yet and render a not-available page; their nav entries stay hidden until
  // this is turned on (ALL-5726; Monitoring and Reconciliation added by ALL-5830).
  deferred_pages: false,
  // Stablecoin invoicing (ALL-5908): the service, `alloy-invoicing-service`, does not
  // exist yet, so the console and public checkout routes ship dark. The data layer
  // behind them is real (it calls the merged contract routes), but no page is visible
  // and no nav entry is shown until this is turned on at build time.
  invoicing: false,
};

/** Parses a build-time flag value; exported so the parsing is unit-testable. */
export function parseEnvFlag(raw: string | undefined): boolean | null {
  if (raw === undefined) return null;
  return raw === "1" || raw.toLowerCase() === "true" || raw.toLowerCase() === "on";
}

function envFlag(flag: FeatureFlag): boolean | null {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return parseEnvFlag(env?.[`VITE_FEATURE_${flag.toUpperCase()}`]);
}

/** Resolves a flag: build-time env var > compiled-in default. No runtime override. */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  const fromEnv = envFlag(flag);
  if (fromEnv !== null) return fromEnv;
  return DEFAULTS[flag];
}
